import React, { Dispatch, FormEvent, SetStateAction } from 'react';
import { mutate } from 'swr';
import { confirmDialog } from 'primereact/confirmdialog';
import { PrimeIcons } from 'primereact/api';
import { ClientRequest } from '../../../hooks/useClient';
import { Routine, ROUTINE_TYPES } from '../../../types/Fisiofit/routine';
import { MUSCLE_GROUPS, EXERCISE_CATEGORIES } from '../../../types/Fisiofit/fisiofitConst';
import { Organization } from '../../../types/organization';
import { User } from '../../../types/user';
import { flattenExercisesForBackend } from '../../../utils/supersetUtils';
import { RoutineForm } from '../validators';
import { RoutinePanelProps } from '../types';
import { getToastMessages } from '../utils/messages';

interface UseRoutineRequestsOptions {
  form: RoutineForm;
  organization: Organization | undefined;
  user: User | undefined;
  post: ClientRequest;
  patch: ClientRequest;
  currentRoutine: Routine | undefined;
  resource: Routine | undefined;
  isCreating: boolean;
  isEditing: boolean;
  isNextRoutine: boolean;
  hasNextRoutine: boolean;
  hasChanged: boolean;
  validate: () => unknown;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  resetForm: () => void;
  onSaveSuccess: RoutinePanelProps['onSaveSuccess'];
  onHide: () => void;
  onDeleteResource: RoutinePanelProps['onDeleteResource'];
  setSelectedExerciseMuscleGroups: Dispatch<SetStateAction<MUSCLE_GROUPS[]>>;
  setSelectedExerciseCategories: Dispatch<SetStateAction<EXERCISE_CATEGORIES[]>>;
}

export const useRoutineRequests = ({
  form,
  organization,
  user,
  post,
  patch,
  currentRoutine,
  resource,
  isCreating,
  isEditing,
  isNextRoutine,
  hasNextRoutine,
  hasChanged,
  validate,
  setIsSaving,
  resetForm,
  onSaveSuccess,
  onHide,
  onDeleteResource,
  setSelectedExerciseMuscleGroups,
  setSelectedExerciseCategories,
}: UseRoutineRequestsOptions) => {
  const handleSave = async (e?: FormEvent): Promise<boolean> => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Verificar si el formulario ha cambiado
    if (!hasChanged) {
      // No se detectaron cambios en el formulario
    }

    const errors = validate();
    console.log('errors', errors);
    if (!form || !organization || errors) return false;
    setIsSaving(true);

    const { externalUser, exerciseDays, routineType, localStartDate, duration, ...restForm } = form;
    const parsedExerciseDays = exerciseDays?.map((day) => {
      const { id, ...restDay } = day;
      return {
        ...restDay,
        exercises: flattenExercisesForBackend(day.exercises),
      };
    });
    const body = {
      ...restForm,
      exerciseDays: parsedExerciseDays,
      ...(routineType === ROUTINE_TYPES.USER ? { localStartDate, duration } : {}),
      ...(externalUser ? { externalUserId: externalUser.id } : {}),
      ...(isCreating ? { organizationId: organization?.id } : {}),
      ...(isCreating && isNextRoutine ? { previousRoutineId: resource?.id } : {}),
    };

    const { successMessage, errorMessages } = getToastMessages(isEditing);
    const request = {
      body,
      successMessage,
      errorMessages,
    };
    const response =
      isEditing && currentRoutine
        ? await patch<Routine>(`/routines/${currentRoutine.id}`, request)
        : await post<Routine>(`/routines`, request);
    if (response) {
      mutate(`/organizations/${organization?.id}/routines`);
      // The Rutinas table reads `.../routines/paginated?page=…` (useServerTable): revalidate it, whatever page, sort or filter (#372).
      const tableKey = `/organizations/${organization?.id}/routines/paginated?`;
      mutate((key) => typeof key === 'string' && key.startsWith(tableKey));
      onSaveSuccess?.();
      if (user) {
        mutate(`/users/${user.id}/routine-alert`);
      }
      // Resetear los filtros después de guardar exitosamente
      setSelectedExerciseMuscleGroups([]);
      setSelectedExerciseCategories([]);
    }

    setIsSaving(false);
    return !!response;
  };

  const handleFinishRoutine = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentRoutine?.id || !organization?.id) return;

    confirmDialog({
      message: `¿Estás seguro de que quieres finalizar esta rutina?\n\n La rutina se terminará, pero se podrá ver en el historial.${hasNextRoutine ? ` La próxima rutina pasará a ser la rutina actual.` : ''}`,
      header: 'Confirmar finalización',
      icon: PrimeIcons.EXCLAMATION_CIRCLE,
      acceptLabel: 'Sí, finalizar',
      rejectLabel: 'Volver',
      draggable: false,
      resizable: false,
      accept: async () => {
        const response = await post(`/routines/${currentRoutine.id}/finish`, {
          successMessage: {
            summary: 'Rutina finalizada',
            detail: 'La rutina se ha finalizado correctamente',
          },
          errorMessages: {
            summary: 'Error al finalizar la rutina',
            defaultDetail: 'No se ha podido finalizar la rutina',
          },
          handlers: {
            defaultSuccess: () => {
              mutate(`/organizations/${organization.id}/routines`);
              const tableKey = `/organizations/${organization.id}/routines/paginated?`;
              mutate((key) => typeof key === 'string' && key.startsWith(tableKey));
              onSaveSuccess?.();
              onHide();
              resetForm();
              if (user) {
                mutate(`/users/${user.id}/routine-alert`);
              }
            },
          },
        });
        return !!response;
      },
      acceptClassName: 'p-button-danger p-button-text',
      rejectClassName: 'p-button-text',
    });
  };

  const handleDelete = async () => {
    const deleted = (await onDeleteResource?.(currentRoutine?.id)) ?? false;
    if (deleted && user?.id) {
      mutate(`/users/${user?.id}/routine-alert`);
    }
    return deleted;
  };

  return { handleSave, handleFinishRoutine, handleDelete };
};
